/**
 * Shared timestamp helpers (UTC parsing, Europe/London formatting).
 *
 * Extracted from QualityPage so the run-status timeline panel can share
 * them; scrape timestamps are stored and served as UTC, while start /
 * finish times are displayed as UK wall-clock time.
 */

/**
 * Parse a UTC ISO timestamp defensively.
 *
 * The API serialises timestamps with an explicit +00:00 offset; older
 * cached responses may lack it, in which case a Z is appended so the
 * value is never misread as browser-local time.
 *
 * @param {string} ts  ISO timestamp string (UTC).
 * @returns {Date}
 */
export function parseUtcTimestamp(ts) {
  const hasOffset = ts.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(ts);
  return new Date(hasOffset ? ts : `${ts}Z`);
}

/** Formatter for date+time cells — UK wall-clock time. */
const LONDON_TIME_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** Formatter for time-only labels — UK wall-clock time. */
const LONDON_CLOCK_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/**
 * Format a UTC ISO timestamp as UK date+time (BST/GMT as appropriate).
 *
 * @param {string|null} ts  ISO timestamp string (UTC).
 * @returns {string}
 */
export function formatLondonTimestamp(ts) {
  if (!ts) return '—';
  const parts = LONDON_TIME_FORMAT.formatToParts(parseUtcTimestamp(ts));
  const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')} ${get('month')} ${get('hour')}:${get('minute')}`;
}

/**
 * Format a UTC ISO timestamp as a UK wall-clock time (HH:MM).
 *
 * @param {string|null} ts  ISO timestamp string (UTC).
 * @returns {string}
 */
export function formatLondonClock(ts) {
  if (!ts) return '—';
  return LONDON_CLOCK_FORMAT.format(parseUtcTimestamp(ts));
}
